import type { PDFWorker } from 'pdfjs-dist';

export type PdfJs = typeof import('pdfjs-dist');

export type PdfJsWithWorker = { pdfjs: PdfJs; worker: PDFWorker };

let pdfjs: Promise<PdfJs> | null = null;
let withWorker: Promise<PdfJsWithWorker> | null = null;

/**
 * pdf.js is most of a megabyte and only the form editor uses it, so it is
 * fetched the first time an editor is wanted rather than with the app.
 */
export const loadPdfJs = (): Promise<PdfJs> => {
  pdfjs ??= Promise.all([import('pdfjs-dist'), import('pdfjs-dist/build/pdf.worker.min.mjs?url')])
    .then(([lib, worker]) => {
      lib.GlobalWorkerOptions.workerSrc = worker.default;
      return lib;
    })
    .catch((error: unknown) => {
      pdfjs = null;
      throw error;
    });
  return pdfjs;
};

/**
 * pdf.js with the worker every document shares. getDocument would otherwise
 * start a fresh worker per form, compiling its 1.4 MB script each time, and
 * terminate it with the document. It starts only for a form the editor will
 * edit, so a page that never edits one does not keep a worker for nothing.
 */
export const loadPdfJsWorker = (): Promise<PdfJsWithWorker> => {
  withWorker ??= loadPdfJs()
    .then(async (lib) => {
      const worker = new lib.PDFWorker();
      try {
        await worker.promise;
      } catch (error) {
        worker.destroy();
        throw error;
      }
      return { pdfjs: lib, worker };
    })
    .catch((error: unknown) => {
      withWorker = null;
      throw error;
    });
  return withWorker;
};

export type PdfWidgetKind = 'text' | 'checkbox' | 'radio' | 'choice';

export type PdfWidget = {
  id: string;
  /** The fully qualified field name, which the editor state calls widgetName. */
  fieldName: string;
  kind: PdfWidgetKind;
  /** The widget's box in CSS pixels at scale 1, from the page's top left corner. */
  left: number;
  top: number;
  width: number;
  height: number;
  /** What the PDF itself holds, shown for widgets the editor does not bind. */
  printedValue: string;
  printedChecked: boolean;
  multiLine: boolean;
  textAlign: 'left' | 'center' | 'right';
  /** In points; 0 asks for the size that fits the box. */
  fontSize: number;
};

/** The part of pdf.js's widget annotation data the editor reads. */
export type WidgetAnnotation = {
  subtype?: string;
  id: string;
  fieldName?: string;
  fieldType?: string | null;
  rect: number[];
  hidden?: boolean;
  noHTML?: boolean;
  hasOwnCanvas?: boolean;
  checkBox?: boolean;
  radioButton?: boolean;
  fieldValue?: unknown;
  exportValue?: string;
  buttonValue?: string;
  multiLine?: boolean;
  textAlignment?: number | null;
  defaultAppearanceData?: { fontSize?: number } | null;
};

const TEXT_ALIGN = ['left', 'center', 'right'] as const;

const widgetKind = (annotation: WidgetAnnotation): PdfWidgetKind | null => {
  switch (annotation.fieldType) {
    case 'Tx':
      return 'text';
    case 'Ch':
      return 'choice';
    case 'Btn':
      if (annotation.checkBox) return 'checkbox';
      if (annotation.radioButton) return 'radio';
      return null;
    default:
      return null;
  }
};

const printedText = (value: unknown) => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.filter((v) => typeof v === 'string').join(', ');
  return '';
};

/**
 * The widgets pdf.js leaves off the canvas when a page renders with
 * AnnotationMode.ENABLE_FORMS, which the editor therefore draws itself.
 * Read-only text and signatures stay on the canvas (hasOwnCanvas, or a field
 * type other than text, choice or toggle button), and push buttons hold no
 * value.
 */
export const widgetsFromAnnotations = (
  annotations: readonly WidgetAnnotation[],
  toViewportRect: (rect: number[]) => number[]
): PdfWidget[] => {
  const widgets: PdfWidget[] = [];
  for (const annotation of annotations) {
    if (annotation.subtype !== 'Widget' || !annotation.fieldName) continue;
    if (annotation.hidden || annotation.noHTML || annotation.hasOwnCanvas) continue;
    const kind = widgetKind(annotation);
    if (!kind) continue;

    const [x1, y1, x2, y2] = toViewportRect(annotation.rect);
    let printedChecked = false;
    if (kind === 'checkbox') {
      printedChecked =
        annotation.exportValue !== undefined && annotation.fieldValue === annotation.exportValue;
    } else if (kind === 'radio') {
      printedChecked =
        annotation.buttonValue !== undefined && annotation.fieldValue === annotation.buttonValue;
    }
    widgets.push({
      id: annotation.id,
      fieldName: annotation.fieldName,
      kind,
      left: Math.min(x1, x2),
      top: Math.min(y1, y2),
      width: Math.abs(x2 - x1),
      height: Math.abs(y2 - y1),
      printedValue: kind === 'text' || kind === 'choice' ? printedText(annotation.fieldValue) : '',
      printedChecked,
      multiLine: !!annotation.multiLine,
      textAlign: TEXT_ALIGN[annotation.textAlignment ?? 0] ?? 'left',
      fontSize: annotation.defaultAppearanceData?.fontSize ?? 0
    });
  }
  return widgets;
};

/** What a page of the PDF prints, as the editor state plans it. */
export type TemplateFormPagePlan = { sheet?: string | null };

/** The numbers pdftk puts in front of a sheet's field names, e.g. "2." */
const JOIN_PREFIX = /^(\d+\.)+/;

/**
 * The fields each page binds, by the PDF's widget name. The plan names the
 * sheet each page is part of, and a sheet's page binds the fields the editor
 * state names behind that sheet's ID; pages past the plan are files attached
 * behind the form and bind none. Without a plan that covers the PDF only the
 * form's own fields bind, by name alone, and a sheet can repeat one of their
 * names, because pdftk renames a sheet's fields only when the first of them
 * collides. The form's own pages come first, so a field binds only on the
 * first page that carries it.
 */
export const fieldsByPage = <Field>(
  pages: readonly { widgets: readonly PdfWidget[] }[],
  fields: ReadonlyMap<string, Field>,
  plan: readonly TemplateFormPagePlan[] | null
): ReadonlyMap<string, Field>[] => {
  const planned = plan !== null && plan.length <= pages.length;
  const claimed = new Set<string>();
  return pages.map(({ widgets }, index) => {
    const bound = new Map<string, Field>();
    if (planned && index >= plan.length) return bound;
    const sheet = planned ? plan[index].sheet : null;
    const keys: string[] = [];
    for (const widget of widgets) {
      let key = widget.fieldName;
      if (planned) key = widget.fieldName.replace(JOIN_PREFIX, '');
      if (sheet) key = `${sheet}/${key}`;
      const field = fields.get(key);
      if (field === undefined || claimed.has(key)) continue;
      bound.set(widget.fieldName, field);
      keys.push(key);
    }
    for (const key of keys) claimed.add(key);
    return bound;
  });
};
